import { MODULATION_TECHNIQUES } from '@tunasong/music-lib'
import type { ModulationQuizQuestion as Question, ModulationTechnique } from '@tunasong/music-lib'
import { Button } from '@tunasong/ui-lib'
import { useEffect, useRef, type FC } from 'react'
import { useModulationAudition } from './modulation-lab-playback.js'
import styles from './modulation-quiz-question.module.css'

const AUTOPLAY_DELAY_MS = 250

export interface ModulationQuizQuestionProps {
  question: Question
  onAnswer: (picked: ModulationTechnique) => void
  questionNumber: number
  totalQuestions: number
}

/**
 * Quiz question surface. Autoplays the audition on mount; the listener picks
 * one of four techniques. Crucially does NOT reveal source/destination keys
 * during asking — that disclosure is reserved for the feedback surface.
 *
 * Spec: docs/superpowers/specs/2026-05-26-modulation-lab-v1-design.md §6
 */
export const ModulationQuizQuestion: FC<ModulationQuizQuestionProps> = props => {
  const { question, onAnswer, questionNumber, totalQuestions } = props
  const { play, isPlaying } = useModulationAudition({ plan: question.plan })

  // Autoplay on mount + on every new question. The play function is stable
  // for the lifetime of the hook so the effect re-runs only when `question`
  // changes. Ref pattern avoids the exhaustive-deps warning for `play`.
  const playRef = useRef(play)
  playRef.current = play
  useEffect(() => {
    const t = window.setTimeout(() => playRef.current(), AUTOPLAY_DELAY_MS)
    return () => window.clearTimeout(t)
  }, [question])

  return (
    <div className={styles.question}>
      <div className={styles.header}>
        Question {questionNumber} / {totalQuestions}
      </div>
      <Button type="button" onClick={play} disabled={isPlaying}>
        {isPlaying ? 'Playing…' : 'Play again'}
      </Button>
      <div className={styles.answerGrid}>
        {MODULATION_TECHNIQUES.map(tech => (
          <Button key={tech} type="button" variant="outlined" onClick={() => onAnswer(tech)}>
            {tech}
          </Button>
        ))}
      </div>
    </div>
  )
}
